import { EnhancedTableToolbar } from '@/components/core/async-data-table/async-data-table-toolbar';
import {
  DatableColumnType,
  propertyType,
} from '@/components/core/async-data-table/type';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useHttp from '@/hooks/use-http';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MobileOrderCard } from './mobile-order-card';

interface MobileOrderListViewProps {
  dataUrl: string;
  refreshPage: boolean;
  defaultFilterVals: propertyType[];
  FilterComponent?: React.ComponentType<any>;
  filterColumn?: DatableColumnType[];
  showPageHeader?: boolean;
  pageTitle?: string;
  pageSubtitle?: string;
  headerAction?: () => void;
  headerActionText?: string;
  headerActionIcon?: React.ReactNode;
  onOrderClick: (row: any) => void;
  highlightedOrderId?: string | null;
}

export const MobileOrderListView = ({
  dataUrl,
  refreshPage,
  defaultFilterVals,
  FilterComponent,
  filterColumn = [],
  showPageHeader,
  pageTitle,
  pageSubtitle,
  headerAction,
  headerActionText,
  headerActionIcon,
  onOrderClick,
  highlightedOrderId,
}: MobileOrderListViewProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5000);
  const [search, setSearch] = useState('');
  const [propSearch, setPropSearch] = useState<propertyType[]>([]);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('total_amount');
  const { loading, data: requestData, sendRequest } = useHttp();
  const prevSearchRef = useRef(search);
  const prevPropSearchRef = useRef(propSearch);
  const lastRequestKeyRef = useRef<string | null>(null);
  const lastRequestTimeRef = useRef<number>(0);

  const tableData = requestData?.content || requestData || [];
  const totalFilter = requestData?.totalElements || requestData?.length || 0;

  const fetchData = useCallback(async () => {
    const searchChanged = prevSearchRef.current !== search;
    const propSearchChanged =
      JSON.stringify(prevPropSearchRef.current) !== JSON.stringify(propSearch);

    const pageToUse = searchChanged || propSearchChanged ? 0 : page;
    prevSearchRef.current = search;
    prevPropSearchRef.current = propSearch;

    if (pageToUse === 0 && page !== 0) {
      setPage(0);
    }

    const propSearchMap = new Map(propSearch.map((prop: any) => [prop.propName, prop]));
    const combinedSearchProp = [
      ...defaultFilterVals.filter((prop: any) => !propSearchMap.has(prop.propName)),
      ...propSearch
    ];

    const transformedSearchProp = combinedSearchProp
      .filter((prop) => prop.value && prop.value.toString().trim() !== '')
      .map((prop) => ({
        propName: prop.propName,
        value: prop.value ? prop.value.toString() : null,
      }));

    const postData = {
      filterText: search || null,
      searchProp: transformedSearchProp.length > 0 ? transformedSearchProp : null,
      page: pageToUse,
      size: rowsPerPage,
      sortBy: orderBy,
      ordinal: order,
    };

    const requestKey = JSON.stringify(postData);
    const now = Date.now();
    if (
      lastRequestKeyRef.current === requestKey &&
      now - lastRequestTimeRef.current < 300
    ) {
      return;
    }
    lastRequestKeyRef.current = requestKey;
    lastRequestTimeRef.current = now;

    await sendRequest(dataUrl, 'POST', postData);
  }, [dataUrl, search, propSearch, page, rowsPerPage, orderBy, order, defaultFilterVals, sendRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (value: string) => {
    const newRowsPerPage = parseInt(value, 5000);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };


  return (
    <div className="md:hidden w-full space-y-4">
      {/* Optional Page Header */}
      {showPageHeader && (
        <div className="flex flex-col gap-4">
          <div className="space-y-1">
            {pageTitle && (
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{pageTitle}</h1>
            )}
            {pageSubtitle && <p className="text-sm text-gray-600 font-medium">{pageSubtitle}</p>}
          </div>
          {headerAction && headerActionText && (
            <Button
              className="gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-6 py-2.5 w-full sm:w-auto"
              onClick={headerAction}
            >
              {headerActionIcon}
              <span>{headerActionText}</span>
            </Button>
          )}
        </div>
      )}

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Filter Section */}
        {FilterComponent && (
          <EnhancedTableToolbar
            FilterComponent={FilterComponent}
            filterColumn={filterColumn}
            propSearch={propSearch}
            setPropSearch={setPropSearch}
            searchFunction={setSearch}
            searchValue={search}
          />
        )}

        {/* Cards List */}
        <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <div className="text-sm text-gray-500">Loading orders...</div>
              </div>
            </div>
          ) : tableData.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="text-sm text-gray-500 mb-1">No orders found</div>
                <div className="text-xs text-gray-400">Try adjusting your filters</div>
              </div>
            </div>
          ) : (
            tableData.map((row: any) => {
              const rowId = String(row.id || row.orderNumber || '');
              const isHighlighted = highlightedOrderId ? String(highlightedOrderId) === String(rowId) : false;
              return (
                <MobileOrderCard
                  key={rowId}
                  row={row}
                  onClick={() => onOrderClick(row)}
                  isHighlighted={isHighlighted}
                />
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalFilter > 0 && (
          <div className="border-t border-gray-200 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              {/* Left: Compact page info */}
              <div className="text-[10px] text-gray-500">
                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, totalFilter)} / {totalFilter}
              </div>

              {/* Center: Navigation Buttons */}
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePage(null, Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="h-7 px-2.5 text-[10px] min-w-[60px]"
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePage(null, page + 1)}
                  disabled={(page + 1) * rowsPerPage >= totalFilter}
                  className="h-7 px-2.5 text-[10px] min-w-[60px]"
                >
                  Next
                </Button>
              </div>

              {/* Right: Per page selector */}
              <Select
                value={rowsPerPage.toString()}
                onValueChange={handleChangeRowsPerPage}
              >
                <SelectTrigger className="h-7 w-[60px] text-[10px] px-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100, 5000].map((option) => (
                    <SelectItem key={option} value={option.toString()} className="text-xs">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
